import { useState, useEffect } from "react";
import Navigation from "../components/Navigation";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Heart, Video, FileText, Headphones, Dumbbell, Filter, Grid, List } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import FavoriteButton from "../components/FavoriteButton";
import TagManager from "../components/TagManager";
import AddExternalLinkDialog from "../components/AddExternalLinkDialog";

const API = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

export default function Favorites({ user, onLogout }) {
  const [favorites, setFavorites] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState({
    type: "",
    source: ""
  });
  const [viewMode, setViewMode] = useState("grid"); // grid or list
  const [showTagManager, setShowTagManager] = useState(false);
  const [showAddLink, setShowAddLink] = useState(false);
  const [availableTags, setAvailableTags] = useState([]);
  const [showVideoPlayer, setShowVideoPlayer] = useState(false);
  const [showArticleViewer, setShowArticleViewer] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [selectedArticle, setSelectedArticle] = useState(null);

  useEffect(() => {
    fetchFavorites();
    fetchStats();
    fetchTags();
  }, [filter]);

  const fetchFavorites = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      
      if (filter.type) params.append('item_type', filter.type);
      if (filter.source) params.append('item_source', filter.source);
      
      const response = await axios.get(`${API}/api/favorites?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setFavorites(response.data);
    } catch (error) {
      toast.error("Failed to load favorites");
      console.error("Error fetching favorites:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/favorites/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const fetchTags = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/tags`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableTags(response.data);
    } catch (error) {
      console.error("Error fetching tags:", error);
    }
  };

  const getItemIcon = (itemType) => {
    switch (itemType) {
      case 'video': return <Video className="w-5 h-5 text-blue-500" />;
      case 'article': return <FileText className="w-5 h-5 text-green-500" />;
      case 'audio': return <Headphones className="w-5 h-5 text-purple-500" />;
      case 'exercise': return <Dumbbell className="w-5 h-5 text-orange-500" />;
      case 'link': return <Heart className="w-5 h-5 text-indigo-500" />;
      case 'document': return <FileText className="w-5 h-5 text-red-500" />;
      case 'file': return <FileText className="w-5 h-5 text-gray-500" />;
      default: return <Heart className="w-5 h-5 text-red-500" />;
    }
  };

  const getSourceLabel = (source) => {
    switch (source) {
      case 'video_library': return 'Video Library';
      case 'news': return 'News';
      case 'mental_training': return 'Mental Training';
      case 'messages': return 'Messages';
      case 'external': return 'External Link';
      default: return source;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const extractYouTubeVideoId = (url) => {
    const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[7].length === 11) ? match[7] : null;
  };

  const handleOpenVideo = (favorite) => {
    const videoData = {
      title: favorite.item_title,
      creator: favorite.item_data?.creator || 'Unknown',
      duration: favorite.item_data?.duration || 'N/A',
      url: favorite.item_data?.url || '',
      description: favorite.item_data?.description || ''
    };
    setSelectedVideo(videoData);
    setShowVideoPlayer(true);
  };

  const handleCloseVideoPlayer = () => {
    setShowVideoPlayer(false);
    setSelectedVideo(null);
  };

  const handleOpenArticle = (favorite) => {
    const articleData = {
      id: favorite.item_id,
      title: favorite.item_title,
      summary: favorite.item_data?.summary || '',
      source: favorite.item_data?.source || favorite.item_source,
      url: favorite.item_data?.url || '',
      published_date: favorite.item_data?.published_date || favorite.created_at,
      category: favorite.item_data?.category || '',
      tags: favorite.tags || []
    };
    setSelectedArticle(articleData);
    setShowArticleViewer(true);
  };

  const handleCloseArticleViewer = () => {
    setShowArticleViewer(false);
    setSelectedArticle(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100">
      <Navigation user={user} onLogout={onLogout} />
      
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Header */}
        <div className="mb-12 text-center">
          <h1 className="text-2xl sm:text-4xl lg:text-5xl font-bold bg-gradient-to-r from-red-600 via-pink-600 to-rose-600 bg-clip-text text-transparent mb-4">
            My Favorites
          </h1>
          <p className="text-lg text-gray-700 max-w-2xl mx-auto mb-6">
            <Heart className="inline h-5 w-5 text-red-500 mr-2" />
            Your favorited videos, articles, exercises, and audio content in one place
          </p>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <Card className="bg-gradient-to-r from-red-50 to-pink-50">
              <CardContent className="p-6">
                <div className="text-3xl font-bold text-red-600">{stats.total_favorites}</div>
                <div className="text-sm text-gray-600">Total Favorites</div>
              </CardContent>
            </Card>
            
            {Object.entries(stats.by_source || {}).map(([source, data]) => (
              <Card key={source} className="bg-gradient-to-r from-gray-50 to-slate-50">
                <CardContent className="p-6">
                  <div className="text-2xl font-bold text-gray-700">{data.count}</div>
                  <div className="text-sm text-gray-600">{getSourceLabel(source)}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <Button
            onClick={() => setShowAddLink(true)}
            className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
          >
            <Heart className="w-4 h-4 mr-2" />
            Add External Link
          </Button>
          <Button
            onClick={() => setShowTagManager(true)}
            variant="outline"
            className="border-purple-600 text-purple-700 hover:bg-purple-50"
          >
            <Filter className="w-4 h-4 mr-2" />
            Manage Tags
          </Button>
        </div>

        {/* Filters and View Controls */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <div className="flex gap-2 flex-1">
            <select
              value={filter.type}
              onChange={(e) => setFilter(prev => ({ ...prev, type: e.target.value }))}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm"
            >
              <option value="">All Types</option>
              <option value="video">Videos</option>
              <option value="article">Articles</option>
              <option value="exercise">Exercises</option>
              <option value="audio">Audio</option>
              <option value="link">External Links</option>
              <option value="document">Documents</option>
              <option value="file">Files</option>
            </select>
            
            <select
              value={filter.source}
              onChange={(e) => setFilter(prev => ({ ...prev, source: e.target.value }))}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm"
            >
              <option value="">All Sources</option>
              <option value="video_library">Video Library</option>
              <option value="news">News</option>
              <option value="mental_training">Mental Training</option>
              <option value="messages">Messages</option>
              <option value="external">External Links</option>
            </select>

            {availableTags.length > 0 && (
              <select
                value={filter.tags || ""}
                onChange={(e) => setFilter(prev => ({ ...prev, tags: e.target.value }))}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm"
              >
                <option value="">All Tags</option>
                {availableTags.map((tag) => (
                  <option key={tag.id} value={tag.tag_name}>
                    {tag.tag_name} ({tag.usage_count})
                  </option>
                ))}
              </select>
            )}
          </div>
          
          <div className="flex gap-2">
            <Button
              variant={viewMode === 'grid' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('grid')}
              className="flex items-center gap-2"
            >
              <Grid className="w-4 h-4" />
              Grid
            </Button>
            <Button
              variant={viewMode === 'list' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('list')}
              className="flex items-center gap-2"
            >
              <List className="w-4 h-4" />
              List
            </Button>
          </div>
        </div>

        {/* Favorites Content */}
        {loading ? (
          <div className="text-center py-12">Loading favorites...</div>
        ) : favorites.length === 0 ? (
          <Card className="border-0 shadow-lg bg-white">
            <CardContent className="p-12 text-center">
              <Heart className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-700 mb-2">No favorites yet</h3>
              <p className="text-gray-500 mb-6">
                Start exploring and click the heart icon to save your favorite content!
              </p>
              <div className="flex gap-4 justify-center">
                <Button onClick={() => window.location.href = '/video-library'} variant="outline">
                  Browse Videos
                </Button>
                <Button onClick={() => window.location.href = '/news'} variant="outline">
                  Read News
                </Button>
                <Button onClick={() => window.location.href = '/mental-training'} variant="outline">
                  Mental Training
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6' : 'space-y-4'}>
            {favorites.map((favorite) => (
              <Card 
                key={favorite.id} 
                className={`hover:shadow-lg transition-shadow ${viewMode === 'list' ? 'flex' : ''}`}
              >
                {viewMode === 'grid' ? (
                  <>
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          {getItemIcon(favorite.item_type)}
                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold text-sm truncate" title={favorite.item_title}>
                              {favorite.item_title}
                            </h4>
                            <p className="text-xs text-gray-500 capitalize">
                              {favorite.item_type} • {getSourceLabel(favorite.item_source)}
                            </p>
                          </div>
                        </div>
                        <FavoriteButton
                          itemType={favorite.item_type}
                          itemId={favorite.item_id}
                          itemTitle={favorite.item_title}
                          itemSource={favorite.item_source}
                          itemData={favorite.item_data}
                          size="sm"
                        />
                      </div>
                    </CardHeader>
                    
                    <CardContent className="pt-0">
                      {/* Tags */}
                      {favorite.tags && favorite.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-3">
                          {favorite.tags.slice(0, 3).map(tag => {
                            const tagInfo = availableTags.find(t => t.tag_name === tag);
                            return (
                              <span 
                                key={tag} 
                                className="text-xs px-2 py-1 rounded-full border"
                                style={{ 
                                  borderColor: tagInfo?.tag_color || '#3B82F6',
                                  color: tagInfo?.tag_color || '#3B82F6'
                                }}
                              >
                                {tag}
                              </span>
                            );
                          })}
                          {favorite.tags.length > 3 && (
                            <span className="text-xs text-gray-400">
                              +{favorite.tags.length - 3} more
                            </span>
                          )}
                        </div>
                      )}
                      
                      {/* View/Access button for different types */}
                      <div className="mb-3">
                        {favorite.item_type === 'link' && favorite.item_data?.url && (
                          <a
                            href={favorite.item_data.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 hover:underline font-medium"
                          >
                            🔗 Open Link
                          </a>
                        )}
                        
                        {favorite.item_type === 'video' && favorite.item_data?.url && (
                          <Button
                            onClick={() => handleOpenVideo(favorite)}
                            variant="outline"
                            size="sm"
                            className="w-full"
                          >
                            <Video className="w-3 h-3 mr-2" />
                            Watch Video
                          </Button>
                        )}
                        
                        {favorite.item_type === 'exercise' && (
                          <Button
                            onClick={() => window.location.href = '/training'}
                            variant="outline"
                            size="sm"
                            className="w-full"
                          >
                            <Dumbbell className="w-3 h-3 mr-2" />
                            View in Mental Training
                          </Button>
                        )}
                        
                        {favorite.item_type === 'article' && favorite.item_data?.url && (
                          <Button
                            onClick={() => handleOpenArticle(favorite)}
                            variant="outline"
                            size="sm"
                            className="w-full"
                          >
                            <FileText className="w-3 h-3 mr-2" />
                            Read Article
                          </Button>
                        )}
                        
                        {favorite.item_type === 'audio' && favorite.item_data?.url && (
                          <a
                            href={favorite.item_data.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 hover:underline font-medium"
                          >
                            🎧 Play Audio
                          </a>
                        )}
                      </div>
                      
                      <div className="flex justify-between items-center text-xs text-gray-400">
                        <span>Added {formatDate(favorite.created_at)}</span>
                        {favorite.item_data?.duration && (
                          <span>{favorite.item_data.duration}</span>
                        )}
                      </div>
                    </CardContent>
                  </>
                ) : (
                  <div className="flex items-center gap-4 p-6 flex-1">
                    {getItemIcon(favorite.item_type)}
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold truncate" title={favorite.item_title}>
                        {favorite.item_title}
                      </h4>
                      <p className="text-sm text-gray-500 capitalize mb-2">
                        {favorite.item_type} • {getSourceLabel(favorite.item_source)}
                      </p>
                      
                      {/* Access buttons for list view */}
                      <div className="mt-2">
                        {favorite.item_type === 'link' && favorite.item_data?.url && (
                          <a
                            href={favorite.item_data.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-blue-600 hover:text-blue-800 hover:underline font-medium"
                          >
                            🔗 Open Link
                          </a>
                        )}
                        
                        {favorite.item_type === 'video' && (
                          <Button
                            onClick={() => window.location.href = '/videos'}
                            variant="outline"
                            size="sm"
                          >
                            View in Video Library
                          </Button>
                        )}
                        
                        {favorite.item_type === 'exercise' && (
                          <Button
                            onClick={() => window.location.href = '/training'}
                            variant="outline"
                            size="sm"
                          >
                            View in Mental Training
                          </Button>
                        )}
                        
                        {favorite.item_type === 'article' && (
                          <Button
                            onClick={() => window.location.href = '/news'}
                            variant="outline"
                            size="sm"
                          >
                            Read Article
                          </Button>
                        )}
                        
                        {favorite.item_type === 'audio' && favorite.item_data?.url && (
                          <a
                            href={favorite.item_data.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-blue-600 hover:text-blue-800 hover:underline font-medium"
                          >
                            🎧 Play Audio
                          </a>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-gray-400 mb-2">
                        {formatDate(favorite.created_at)}
                      </div>
                      <FavoriteButton
                        itemType={favorite.item_type}
                        itemId={favorite.item_id}
                        itemTitle={favorite.item_title}
                        itemSource={favorite.item_source}
                        itemData={favorite.item_data}
                        size="sm"
                      />
                    </div>
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>
      
      {/* Tag Manager Dialog */}
      <TagManager
        isOpen={showTagManager}
        onClose={() => setShowTagManager(false)}
        onTagsUpdated={() => {
          fetchTags();
          fetchFavorites();
        }}
      />
      
      {/* Add External Link Dialog */}
      <AddExternalLinkDialog
        isOpen={showAddLink}
        onClose={() => setShowAddLink(false)}
        onLinkAdded={() => {
          fetchFavorites();
          fetchStats();
        }}
      />

      {/* Video Player Modal */}
      {showVideoPlayer && selectedVideo && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={handleCloseVideoPlayer}
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-r from-emerald-600 to-teal-600 p-6 text-white">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="text-2xl font-bold mb-2">{selectedVideo.title}</h3>
                  <div className="flex items-center gap-4 text-emerald-100">
                    <span>{selectedVideo.creator}</span>
                    <span>{selectedVideo.duration}</span>
                  </div>
                </div>
                <button
                  onClick={handleCloseVideoPlayer}
                  className="ml-4 p-2 hover:bg-white/20 rounded-full transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="relative bg-black" style={{ paddingTop: '56.25%' }}>
              <iframe
                className="absolute inset-0 w-full h-full"
                src={`https://www.youtube.com/embed/${extractYouTubeVideoId(selectedVideo.url)}?autoplay=1&rel=0&modestbranding=1`}
                title={selectedVideo.title}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
            <div className="p-6 bg-gray-50">
              <p className="text-gray-600">{selectedVideo.description}</p>
              <div className="mt-4">
                <Button
                  onClick={() => window.open(selectedVideo.url, '_blank')}
                  variant="outline"
                >
                  Watch on YouTube
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Article Viewer Modal */}
      {showArticleViewer && selectedArticle && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={handleCloseArticleViewer}
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-r from-teal-600 to-emerald-600 p-6 text-white flex-shrink-0">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="text-2xl font-bold mb-3">{selectedArticle.title}</h3>
                  <div className="flex items-center gap-4 text-teal-100">
                    <span>{selectedArticle.source}</span>
                    {selectedArticle.published_date && (
                      <span>{formatDate(selectedArticle.published_date)}</span>
                    )}
                  </div>
                </div>
                <button
                  onClick={handleCloseArticleViewer}
                  className="ml-4 p-2 hover:bg-white/20 rounded-full transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-8">
              {selectedArticle.summary && (
                <div className="mb-6">
                  <h4 className="font-semibold text-lg mb-3">Summary</h4>
                  <p className="text-gray-700 leading-relaxed">{selectedArticle.summary}</p>
                </div>
              )}
              {selectedArticle.url && (
                <div className="bg-gray-50 border-2 border-gray-200 rounded-xl p-6">
                  <h4 className="font-semibold text-lg mb-4">Full Article</h4>
                  <div className="bg-white rounded-lg border overflow-hidden" style={{ height: '600px' }}>
                    <iframe
                      src={selectedArticle.url}
                      title={selectedArticle.title}
                      className="w-full h-full"
                      frameBorder="0"
                      sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
                    />
                  </div>
                </div>
              )}
            </div>
            <div className="p-6 bg-gray-50 border-t flex gap-3 flex-shrink-0">
              <Button
                onClick={() => window.open(selectedArticle.url, '_blank')}
                className="bg-gradient-to-r from-teal-600 to-emerald-600"
              >
                Open in New Tab
              </Button>
              <Button onClick={handleCloseArticleViewer} variant="outline">
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}